import { useCallback } from 'react';

import { openWindow } from '@change-corgi/core/window';
import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';
import type { TrackingFn } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useMovementPageData } from 'src/app/pages/movement/pageContext';
import { useCurrentUser } from 'src/app/shared/hooks/session';
import { shareFacebook } from 'src/app/shared/share/facebook';
import { shareTwitter } from 'src/app/shared/share/twitter';
import { shareWhatsapp } from 'src/app/shared/share/whatsapp';

import { useShareUrl } from './useSocialShares';

type VoidFunction = () => void;

export const MOVEMENT_EVENT_VIEW_CONTEXT = 'movement_show';

const FB_TRACKING_EVENTS = {
	success: 'movement_share_success',
	intent: 'movement_share_intent',
	legacy_success: 'movement_share',
	cancel: 'movement_share_cancel',
	failure: 'movement_share_failure',
};

const TWITTER_TRACKING_EVENTS = {
	success: 'movement_share',
};

const WHATSAPP_TRACKING_EVENTS = {
	success: 'movement_share',
};

const TRACKING_DATA = {
	context: MOVEMENT_EVENT_VIEW_CONTEXT,
	viewContext: MOVEMENT_EVENT_VIEW_CONTEXT,
};

type MovementPageShareParams = {
	movementId: string;
	location: string;
	viewContext: string;
	context: string;
};

async function trackMovementPageShare(params: MovementPageShareParams, track: TrackingFn): Promise<boolean> {
	const eventName = 'movement_share';
	const eventOptions = {
		movement_id: params.movementId,
		location: params.location,
		context: params.context,
		view_context: params.viewContext,
	};
	return track(eventName, eventOptions);
}

// TODO migrate to using useAvailableShareAction()

export function useShareClickHandlers(): {
	onEmailClick: VoidFunction;
	onFacebookClick: VoidFunction;
	onTwitterClick: VoidFunction;
	onWhatsAppClick: VoidFunction;
} {
	const {
		data: {
			movement: { id: movementId, objective },
		},
	} = useMovementPageData();

	const currentUser = useCurrentUser();
	const utilityContext = useUtilityContext();
	const track = useTracking();
	const shareUrls = useShareUrl({ slug: movementId });
	const { translate } = useI18n();
	const userName = currentUser?.shortDisplayName ?? '';
	const countryCode = currentUser?.country?.countryCode;

	const onFacebookClick = useCallback(() => {
		void shareFacebook(
			shareUrls.facebook,
			{
				trackingData: { ...TRACKING_DATA, location: 'facebook', movement_id: movementId },
				trackingEvents: FB_TRACKING_EVENTS,
			},
			utilityContext,
			track,
		);
	}, [movementId, shareUrls, utilityContext, track]);

	const onTwitterClick = useCallback(
		() =>
			shareTwitter({
				url: shareUrls.twitter,
				comment: translate('corgi.movement.show.share.twitter', { objective }),
				options: {
					trackingData: { ...TRACKING_DATA, location: 'twitter', movement_id: movementId },
					trackingEvents: TWITTER_TRACKING_EVENTS,
					countryCode,
				},
				track,
				utilityContext,
			}),
		[countryCode, movementId, shareUrls, objective, translate, track, utilityContext],
	);

	const onEmailClick = useCallback(() => {
		const encodedSubject = encodeURIComponent(objective);
		const encodedBody = encodeURIComponent(
			translate('corgi.movement.show.share.email_body', {
				objective,
				url: shareUrls.email,
				from: userName,
			}),
		);
		const emailHref = `mailto:?body=${encodedBody}&subject=${encodedSubject}`;
		void trackMovementPageShare(
			{
				...TRACKING_DATA,
				movementId,
				location: 'email',
			},
			track,
		);
		openWindow(emailHref);
	}, [objective, translate, shareUrls.email, userName, movementId, track]);

	const onWhatsAppClick = useCallback(() => {
		shareWhatsapp(
			objective,
			shareUrls.whatsapp,
			{
				trackingEvents: WHATSAPP_TRACKING_EVENTS,
				trackingData: { ...TRACKING_DATA, location: 'whatsapp', movement_id: movementId },
			},
			track,
		);
	}, [objective, shareUrls.whatsapp, movementId, track]);

	return {
		onEmailClick,
		onFacebookClick,
		onTwitterClick,
		onWhatsAppClick,
	};
}
